import { onAnnounce } from "@kovernow/flows/announce";
import { LiveAnnouncer } from "@kovernow/react-ui/a11y/live-announcer";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next";
import { useState, type ReactNode } from "react";
import { RouterProvider, type createBrowserRouter } from "react-router";
import { ServicesProvider } from "./core/app-context";
import type { AppServices } from "./core/services";

export function createQueryClient() {
  return new QueryClient({
    defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } },
  });
}

export function Providers({
  services,
  children,
  queryClient: injected,
}: {
  services: AppServices;
  children: ReactNode;
  /** Tests may pass their own to inspect the cache. */
  queryClient?: QueryClient;
}) {
  // One cache per app mount (and per test render): no data leaks between them.
  const [queryClient] = useState(() => injected ?? createQueryClient());
  return (
    <ServicesProvider value={services}>
      <I18nextProvider i18n={services.i18n}>
        <QueryClientProvider client={queryClient}>
          {children}
          {/* One polite + one assertive region for the whole app, above the
              router: "Signed in/out" outlive the shell swap that follows. */}
          <LiveAnnouncer subscribe={onAnnounce} />
        </QueryClientProvider>
      </I18nextProvider>
    </ServicesProvider>
  );
}

export function App({
  services,
  router,
}: {
  services: AppServices;
  router: ReturnType<typeof createBrowserRouter>;
}) {
  return (
    <Providers services={services}>
      <RouterProvider router={router} />
    </Providers>
  );
}
